import { useEffect, useState } from "react";
import { useDrag } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import { StatusTypes } from "../../../abstract/controller/enum/dnd/StatusTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import { isNonEmptyString } from "../../../abstract/controller/utils/systemUtils";
import * as uuidUtils from "../../../abstract/controller/utils/uuidUtils";
import useStatus from "../../../abstract/hook/useStatus";
import {
  getIsContextMenuDisplayed,
  getIsHighlighted,
  getWindowLock,
  setContextMenu,
  setWindowLock,
} from "../../../abstract/redux/slice/overlaySlice";
import { setDrag, setIdle } from "../../../abstract/redux/slice/statusSlice";
import {
  handleParameterCopy,
  handleParameterDetach,
  handleParameterRemove,
} from "../../../abstract/redux/slice/structureSlice";
import IconArrowClockwise from "../../../themes/icons/IconArrowClockwise";
import IconMenuVertical from "../../../themes/icons/IconMenuVertical";
import IconWindowMaximize2 from "../../../themes/icons/IconWindowMaximize2";
import IconWindowMinimize2 from "../../../themes/icons/IconWindowMinimize2";
import { ButtonOverlay } from "../../ui/buttons/ButtonOverlay";
import Menu from "../../ui/menu/Menu";
import DialogSelect from "../../ui/modal/DialogSelect";
import styles from "./ParameterMain.module.scss";
import IconError from "../../ui/icons/IconError";
import IconPlusCircled from "../../../themes/icons/IconPlusCircled";
import { getErrors, getIsExtended, getIsFolded, setMetaExtended, setMetaFolded } from "../../../abstract/redux/slice/metaSlice";

function ParameterMain({ node, children, iconRight }) {
  const _dispatch = useDispatch();

  const _id = nodeUtils.getID(node.current);
  const _uuid = nodeUtils.getUUID(node.current);

  const _errors = useSelector(getErrors(_uuid))
  const _isFolded = useSelector(getIsFolded(_uuid));
  const _isExtended = useSelector(getIsExtended(_uuid));

  const _status = useStatus(_id);
  const _windowLock = useSelector(getWindowLock);
  const _isHighlighted = useSelector(getIsHighlighted(_id));
  const _isContextMenuDisplayed = useSelector(getIsContextMenuDisplayed(_id));

  const [_isMouseOver, _setMouseOver] = useState(false);
  const [_showModalRef, _setShowModalRef] = useState(false);
  const [_showModalKeep, _setShowModalKeep] = useState(false);

  const _m_uuid = uuidUtils.getMainUuid(_uuid);
  const _name = nodeUtils.getName(node.current);

  const _isDragged = _status === StatusTypes.DRAGGED;
  const _isTop = nodeUtils.isParameterTop(node.current);
  const _isFinal = nodeUtils.hasParameters(node.current);

  const _isDefault = nodeUtils.isParameterDefault(node.current);
  const _isLinked = nodeUtils.isParameterLinked(node.current);

  //---------------------------------------------------------------------

  const [, drag] = useDrag(
    () => ({
      type: ItemTypes.PARAMETER,
      item: { element: node.current },
    }),
    [node]
  );

  //---------------------------------------------------------------------

  function HeaderIconAddLeft({ name, isMouseOver }) {
    const getClass = () => {
      return `${styles["icons-add-left"]}`;
    };

    if (_isDragged || !isMouseOver || !isNonEmptyString(name)) {
      return null;
    }

    return (
      <div className={getClass()}>
        <IconPlusCircled />
      </div>
    );
  }

  function HeaderIconAddRight({ name, isMouseOver, show }) {
    const getClass = () => {
      return `${styles["icons-add-right"]}`;
    };

    if (_isDragged || !isMouseOver || !isNonEmptyString(name) || !show) {
      return null;
    }

    return (
      <div className={getClass()}>
        <IconPlusCircled />
      </div>
    );
  }

  function HeaderIconFold({ id, uuid, name, isMouseOver }) {
    const _dispatch = useDispatch();

    const handleMenuLeftFold = () => {
      _dispatch(setMetaFolded(uuid, !_isFolded));
      _dispatch(setMetaExtended(uuid, false));
    };

    const getClass = () => {
      return `${styles["icons-fold"]}`;
    };

    if (!isMouseOver || !isNonEmptyString(name)) {
      return null;
    }

    return (
      <div className={getClass()} onClick={handleMenuLeftFold}>
        <IconArrowClockwise />
      </div>
    );
  }

  function HeaderIconMeta({ uuid, name, isMouseOver, status, isFolded, isExtended }) {
    const _dispatch = useDispatch();

    const getClassIcon = () => {
      return `${styles["icons-meta"]}`;
    };

    const handleButtonShowOptions = () => {
      _dispatch(setMetaExtended(uuid, !isExtended));
    };

    if (!isMouseOver || isFolded || status === StatusTypes.DRAGGED || !isNonEmptyString(name)) {
      return null;
    }

    return (
      <div className={getClassIcon()}>
        <ButtonOverlay handler={handleButtonShowOptions}>
          {isExtended ? <IconWindowMinimize2 /> : <IconWindowMaximize2 />}
        </ButtonOverlay>
      </div>
    );
  }

  function HeaderIconOptions({ uuid, name, isMouseOver, status, isFolded, toggleContextMenu }) {
    const getClass = () => {
      return `${styles["icons-options"]}`;
    };

    const handleMenuRightToggle = () => {
      toggleContextMenu();
    };

    if (!isMouseOver || isFolded || status === StatusTypes.DRAGGED || !isNonEmptyString(name)) {
      return null;
    }

    return (
      <div className={getClass()} onClick={handleMenuRightToggle}>
        <IconMenuVertical id={`action-${uuid}`} />
      </div>
    );
  }

  function WindowOptions({
    node,
    id,
    isFolded,
    isContextMenuDisplayed,
    clearContextMenu,
    setShowModalRef,
    setShowModalKeep,
  }) {
    const _dispatch = useDispatch();
    const _windowLock = useSelector(getWindowLock);

    const handleButtonRemoveParameter = () => {
      proceedWithNormalRemoval();

      // const parentId = pathUtils.getIdParent(id);

      // if (parentId) {
      //   let hasProcessed = false;
      //   let timeoutId;

      //   // Set up listener for proceed event BEFORE dispatching the warning
      //   const handleProceed = (event) => {
      //     if (event.detail.parameterId === id && !hasProcessed) {
      //       hasProcessed = true; // Mark as processed
      //       clearTimeout(timeoutId); // Clear the timeout
      //       window.removeEventListener("constraint-warning-proceed", handleProceed);
      //       window.removeEventListener("constraint-warning-cancel", handleCancel);
      //       proceedWithNormalRemoval();
      //     }
      //   };

      //   //  Set up listener for cancel event (when modal is shown)
      //   const handleCancel = (event) => {
      //     if (event.detail.parameterId === id && !hasProcessed) {
      //       hasProcessed = true; // Mark as processed
      //       clearTimeout(timeoutId); // Clear the timeout
      //       window.removeEventListener("constraint-warning-proceed", handleProceed);
      //       window.removeEventListener("constraint-warning-cancel", handleCancel);
      //     }
      //   };

      //   window.addEventListener("constraint-warning-proceed", handleProceed);
      //   window.addEventListener("constraint-warning-cancel", handleCancel); // NEW

      //   window.dispatchEvent(
      //     new CustomEvent("constraint-warning", {
      //       detail: { parentId, parameterId: id },
      //     })
      //   );

      //   // Set a timeout as fallback (in case no parent responds)
      //   timeoutId = setTimeout(() => {
      //     if (!hasProcessed) {
      //       hasProcessed = true; // Mark as processed
      //       window.removeEventListener("constraint-warning-proceed", handleProceed);
      //       window.removeEventListener("constraint-warning-cancel", handleCancel);
      //       proceedWithNormalRemoval();
      //     }
      //   }, 100);

      //   return;
      // }

      // // If no parent, proceed with normal removal
      // proceedWithNormalRemoval();
    };

    // Extract the original removal logic into a separate function
    const proceedWithNormalRemoval = () => {
      if (nodeUtils.getIdReferences(node.current).length > 0) {
        _dispatch(setWindowLock(true));
        setShowModalRef(true);
      } else {
        if (nodeUtils.hasParameters(node.current)) {
          _dispatch(setWindowLock(true));
          setShowModalKeep(true);
        } else {
          _dispatch(handleParameterRemove(id));
        }
      }
    };

    const handleButtonCopyParameter = () => {
      _dispatch(handleParameterCopy(id));
      clearContextMenu();
    };

    const handleButtonDetachParameter = () => {
      _dispatch(handleParameterDetach(id));
      clearContextMenu();
    };

    const getActions = () => {
      const _actions = [];

      _actions.push({ name: "remove", action: handleButtonRemoveParameter });
      _actions.push({ name: "copy", action: handleButtonCopyParameter });

      if (nodeUtils.getIdTarget(node.current)) {
        _actions.push({ name: "detach", action: handleButtonDetachParameter });
      }

      return _actions;
    };

    if (_windowLock || isFolded) {
      return null;
    }

    return <Menu show={isContextMenuDisplayed} items={getActions()} />;
  }

  function WindowModalRemoveOptions({
    node,
    id,
    showModalRef,
    setShowModalRef,
    showModalKeep,
    setShowModalKeep,
  }) {
    const _dispatch = useDispatch();

    return (
      <>
        {showModalRef && (
          <DialogSelect
            textHeader="remove options"
            textBody={`The selected parameter is the target of '${
              nodeUtils.getIdReferences(node)?.length
            }' linked parameters. Decide what to do with them.`}
            actions={[
              {
                text: "delete",
                handler: () => {
                  _dispatch(handleParameterRemove(id, false, true));
                  _dispatch(setWindowLock(false));
                  setShowModalRef(false);
                },
              },
              {
                text: "detach",
                handler: () => {
                  _dispatch(handleParameterRemove(id, false, false));
                  _dispatch(setWindowLock(false));
                  setShowModalRef(false);
                },
              },
              {
                text: "cancel",
                handler: () => {
                  _dispatch(setWindowLock(false));
                  setShowModalRef(false);
                },
              },
            ]}
            cancel={() => {
              _dispatch(setWindowLock(false));
              setShowModalRef(false);
            }}
          />
        )}
        {showModalKeep && (
          <DialogSelect
            textHeader="remove options"
            textBody={`Decide what to do with nested parameters.`}
            actions={[
              {
                text: "delete",
                handler: () => {
                  _dispatch(handleParameterRemove(id, false, false));
                  _dispatch(setWindowLock(false));
                  setShowModalKeep(false);
                },
              },
              {
                text: "keep",
                handler: () => {
                  _dispatch(handleParameterRemove(id, true, false));
                  _dispatch(setWindowLock(false));
                  setShowModalKeep(false);
                },
              },
              {
                text: "cancel",
                handler: () => {
                  _dispatch(setWindowLock(false));
                  setShowModalKeep(false);
                },
              },
            ]}
            cancel={() => {
              _dispatch(setWindowLock(false));
              setShowModalKeep(false);
            }}
          />
        )}
      </>
    );
  }

  const clearContextMenu = () => {
    if (_isContextMenuDisplayed) {
      _dispatch(setContextMenu(""));
    }
  };

  const toggleContextMenu = () => {
    _dispatch(setContextMenu(_isContextMenuDisplayed ? "" : _id));
  };

  useEffect(() => {
    if (_status === StatusTypes.DRAGGED || _status === StatusTypes.POSITIONED) {
      if (_isContextMenuDisplayed) {
        _dispatch(setContextMenu(""));
      }
    }
  }, [_isContextMenuDisplayed, _status, _dispatch]);

  //---------------------------------------------------------------------

  const getClassPosition = () => {
    return _isTop ? styles["position--top"] : styles["position--nested"];
  };

  const getClassFold = () => {
    return _isFolded ? styles["position--folded"] : styles["position--unfolded"];
  };

  const getClassState = () => {
    if (_isDragged) {
      return styles["style--dragged"];
    }

    return _name ? styles["style--default"] : styles["style--ephemeral"];
  };

  const getClassAnimation = () => {
    return _name ? "" : styles["style--animated"];
  };

  const getClassBackground = () => {
    if (_isHighlighted) {
      return styles["style--highlight"];
    }

    return _windowLock || _isDragged || !_isMouseOver || !_name ? "" : styles["style--hover"];
  };

  const getClassLevel = () => {
    if (_isFinal) {
      return styles[`style--nesting-0`];
    }

    return styles[`style--nesting-limit`];
  };

  const getClassFunction = () => {
    if (_isDefault) {
      return styles["style--output"];
    }

    return "";
  };

  const getClass = () => {
    return `${
      styles["position"]
    } ${getClassLevel()} ${getClassPosition()} ${getClassState()} ${getClassAnimation()} ${getClassBackground()} ${getClassFold()} ${getClassFunction()}`;
  };

  //---------------------------------------------------------------------

  const handleOnDragStart = (e) => {
    if (e.target.id.includes(_uuid)) {
      const _element = document.querySelector(`#${_m_uuid}`);
      const _size = _element.getBoundingClientRect();
      _dispatch(setDrag({ id: _id, size: { width: _size.width, height: _size.height } }));
    }
  };

  const handleOnDragEnd = () => {
    _dispatch(setIdle());
  };

  const canDrag = () => {
    return !_isFolded && _name;
  };

  const handleOnMouseOver = (e) => {
    e.stopPropagation();

    if (_windowLock) {
      return;
    }

    _setMouseOver(true);
  };

  const handleOnMouseOut = (e) => {
    e.stopPropagation();

    if (_windowLock) {
      return;
    }

    _setMouseOver(false);
  };

  //---------------------------------------------------------------------

  return (
    <>
      <div
        id={_m_uuid}
        ref={canDrag() ? drag : null}
        className={getClass()}
        onDragStart={handleOnDragStart}
        onDragEnd={handleOnDragEnd}
        onMouseOver={handleOnMouseOver}
        onMouseOut={handleOnMouseOut}
        >
        {_isDefault && <div className={styles["legend"]}>Output</div>}
        {_isLinked && <div className={styles["legend"]}>Linked</div>}
        <div className={styles["icon-error"]}>
          <IconError uuid={_uuid} errors={_errors} />
        </div>
        <HeaderIconAddLeft name={_name} isMouseOver={_isMouseOver} />
        <HeaderIconAddRight name={_name} isMouseOver={_isMouseOver} show={iconRight} />
        <HeaderIconFold id={_id} uuid={_uuid} name={_name} isMouseOver={_isMouseOver} />
        <HeaderIconMeta
          uuid={_uuid}
          name={_name}
          isMouseOver={_isMouseOver}
          status={_status}
          isFolded={_isFolded}
          isExtended={_isExtended}
        />
        <HeaderIconOptions
        uuid={_uuid}
          name={_name}
          isMouseOver={_isMouseOver}
          status={_status}
          isFolded={_isFolded}
          toggleContextMenu={toggleContextMenu}
        />
        {children}
        <WindowOptions
          node={node}
          id={_id}
          isFolded={_isFolded}
          isContextMenuDisplayed={_isContextMenuDisplayed}
          clearContextMenu={clearContextMenu}
          setShowModalRef={_setShowModalRef}
          setShowModalKeep={_setShowModalKeep}
        />
        <WindowModalRemoveOptions
          node={node}
          id={_id}
          showModalRef={_showModalRef}
          setShowModalRef={_setShowModalRef}
          showModalKeep={_showModalKeep}
          setShowModalKeep={_setShowModalKeep}
        />
      </div>
    </>
  );
}

export default ParameterMain;
